import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from '@uhg-abyss/web/tools/reactRouterTools/reactRouterDom';
import { styled } from '@uhg-abyss/web/tools/styled';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { useTIN } from '@src/state/taxInformation/hooks';
import { useEditing } from '@src/state/editing/hooks';
import { useVerifyClaim } from '@src/state/verifyClaim';
import { useUser } from '@src/state/user/index';
import { LoadingOverlay } from '@uhg-abyss/web/ui/LoadingOverlay';
import { usePAARegistration } from '@src/state/PAARegistration/hooks';
import { event as adobeTaggingEvent } from '@uhg-abyss/web/tools/event';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { useFormState } from '@src/state/form/hooks';
import { updateCompanyName } from '@src/state/form/actions';
import { config } from '@uhg-abyss/web/tools/config';
import { LagoonContext } from '@src/AppContext/LagoonContext';
import { useDental } from '@src/state/dental/hooks';
import { setPageHandler } from '@src/common/tools/analytics/helpers';
import { adobeInteraction, getOrgType } from '../../../../common/helpers';

const MainContainer = styled('div', {
  border: '1px solid rgb(219, 219, 219)',
  background: '$white',
  padding: '25px',
  marginBottom: '20px',
});

const BoxHeader = styled('div', {
  position: 'relative',
  marginBottom: '10px',
});

const BoxHeaderText = styled('h3', {
  fontWeight: 600,
  fontSize: '24px',
  width: '100%',
  color: '$primary1',
  fontFamily: '$heading',
});

const Hint = styled('p', {
  fontSize: '14px',
  fontWeight: 500,
  fontStyle: 'italic',
  marginBottom: '10px',
  fontFamily: '$primary',
});

const BackButton = styled('div', {
  width: '115px',
  padding: '6px',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  borderWidth: '2.5px',
  border: '2px solid $primary1',
  background: '$white',
  borderStyle: 'solid',
  borderRadius: '23px',
  marginTop: 'auto',
  marginBottom: '20px',
  alignItems: 'center',
  '&:hover': {
    cursor: 'pointer',
  },
});

const NextButton = styled('div', {
  float: 'right',
  width: '115px',
  padding: '6px',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'row',
  borderWidth: '2.5px',
  border: '2px solid $primary1',
  background: '$primary1',
  borderStyle: 'solid',
  borderRadius: '23px',
  marginTop: 'auto',
  marginBottom: '20px',
  alignItems: 'center',
  '&:hover': {
    cursor: 'pointer',
  },
});

const BackButtonText = styled('text', {
  color: '$primary1',
  fontWeight: 'bold',
  marginLeft: '12px',
  fontSize: '16px',
});

const NextButtonText = styled('text', {
  color: '$white',
  fontWeight: 'bold',
  marginRight: '12px',
  fontSize: '16px',
});

export const CompanyVerification = ({
  currentFlow,
  handleBack,
  completeStep,
}) => {
  const form = useForm();
  const { token } = useParams();
  const [companyName, setCompanyName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const { userUUID } = useUser.state();
  const { submitTINRequest } = usePAARegistration.actions();
  const lagoonContext = React.useContext(LagoonContext);
  const featureToggles = lagoonContext.getTable('feature-toggles');
  const uiMessages = lagoonContext.getTable('ui-messaging');
  const errorMessages = lagoonContext.getTable('error-messaging');

  const { apiListSelection, apiUseText, phone, phoneExt, selectedApiCodes } =
    usePAARegistration.state();
  const { TINNumber, TINInfo } = useTIN.state();
  const router = useRouter();
  const { actions, state } = useFormState({ updateCompanyName });
  const { isDental } = useDental.state();

  useEffect(() => {
    form.setValue(
      'CompanyVerification.companyName',
      state?.registration?.companyName || ''
    );
  }, []);

  useEffect(() => {
    if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
      const orgType = getOrgType(currentFlow);
      const section1 = 'paa registration';
      const section2 = 'company verification';
      const pageName = `digitalsecurity:${section1}:${section2}`;
      setPageHandler('company verification');
      adobeTaggingEvent('ON_PAGE_LOAD', {
        pageName,
        orgType,
        organization: TINInfo?.corpName,
        uuid: userUUID,
        tin: TINNumber,
        section1,
        section2,
        section3: '',
        siteSectionL3: 'paa registration',
      });
    }
  }, []);

  const { isEditing } = useEditing.state();

  const { verifyCompanyName } = useVerifyClaim.actions();
  const { verifyCompanyNameSaga } = useVerifyClaim.state();

  const processResponse = (apiResponse) => {
    if (apiResponse.error) {
      if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
        adobeTaggingEvent('API_TRANSACTION_ERROR', {
          errorMessage: apiResponse.error.description,
          errorApiCode: apiResponse.error.code,
          errorHttpsCode: apiResponse.error.status,
        });
      }
      const { code } = apiResponse.error;
      setErrorMessage(
        (code && errorMessages.getRow(code)?.message) ||
          errorMessages.getRow('DEFAULT')?.message
      );
      return;
    }
    router.navigate(`/PAARegistration/verificationPending/${token}`);
    adobeInteraction(
      'Company Verification',
      !isEditing ? 'next' : 'save',
      getOrgType(currentFlow),
      featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active
    );
    completeStep();
  };

  const handleSubmit = () => {
    form.validate('CompanyVerification.companyName');
    form.validate(
      'CompanyVerification',
      (formModel, fields) => {
        const { companyName: businessName } = formModel;
        if (currentFlow.includes('API marketplace')) {
          const selectedAPICodesv1 = apiListSelection
            .filter((api) => {
              return api.selected;
            })
            .map((api) => {
              return api.code;
            });
          submitTINRequest(
            {
              url:
                uiMessages.getRow('REGISTER_API')?.message ||
                config('REGISTER_API'),
              requestId: TINInfo.requestId,
              currentFlow,
              businessName,
              apiRequested: featureToggles.getRow('US8482146_SUPER_CONNECTOR')
                ?.active
                ? selectedApiCodes
                : selectedAPICodesv1,
              comments: apiUseText,
              phoneNumber: phone,
              phoneExt,
              isDental,
            },
            processResponse
          );
          return;
        }
        const payload = {
          currentFlow,
          tin: TINNumber,
          requestId: TINInfo.requestId,
          userUUID,
          businessName,
          phoneNumber: phone,
          phoneExt,
          url:
            uiMessages.getRow('REGISTER_API')?.message ||
            config('REGISTER_API'),
        };

        verifyCompanyName(payload, processResponse, (errorResponse) => {
          if (!errorResponse.error) return;
          const { code } = errorResponse.error;
          setErrorMessage(
            (code && errorMessages.getRow(code))?.message ||
              errorMessages.getRow('DEFAULT')?.message
          );
        });
      },
      (error) => {
        if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
          const fields = ['Company Name'];
          adobeTaggingEvent('FORM_VALIDATION_ERROR', { fields });
        }
      }
    );
  };

  return (
    <React.Fragment>
      <LoadingOverlay isLoading={verifyCompanyNameSaga.isLoading}>
        <MainContainer>
          <Flex.Content>
            <BoxHeader>
              <BoxHeaderText>
                <span>To verify your organization, please enter</span>
                <br />
                <span>the company name as it appears in your W-9 Form.</span>
              </BoxHeaderText>
            </BoxHeader>
          </Flex.Content>
          <Flex.Content>
            <FormProvider state={form} style={{ width: '70%' }}>
              <TextInput
                id="CompanyVerification.companyName"
                model="CompanyVerification.companyName"
                label="Company Name"
                aria-label=""
                validators={{
                  required: 'Company Name is required',
                }}
                onChange={(e) => {
                  setErrorMessage('');
                  setCompanyName(e.target.value);
                  actions.updateCompanyName({
                    companyName: e.target.value,
                  });
                }}
                onClear={(e) => {
                  setErrorMessage('');
                  setCompanyName('');
                }}
                isClearable
                value={companyName}
              />
            </FormProvider>
            <Hint>As it appears on your W-9 Form</Hint>
            {errorMessage ? (
              <div style={{ color: '#d0021c', fontSize: '12px' }}>
                {errorMessage}
              </div>
            ) : null}
          </Flex.Content>
        </MainContainer>
        <Flex
          css={{
            'abyss-flex-root': {
              justifyContent: 'space-between',
            },
          }}
        >
          <Flex.Content space-50>
            {!isEditing && (
              <BackButton onClick={() => handleBack('company verification')}>
                <IconSymbol icon="arrow_back" size={20} color="$primary1" />
                <BackButtonText>Back</BackButtonText>
              </BackButton>
            )}
          </Flex.Content>
          <Flex.Content space-50>
            <NextButton onClick={handleSubmit}>
              <NextButtonText>{!isEditing ? 'Next' : 'Save'}</NextButtonText>
              <IconSymbol icon="arrow_forward" size={16} color="$primary2" />
            </NextButton>
          </Flex.Content>
        </Flex>
      </LoadingOverlay>
    </React.Fragment>
  );
};

CompanyVerification.propTypes = {
  currentFlow: PropTypes.string.isRequired,
  handleBack: PropTypes.func.isRequired,
  completeStep: PropTypes.func.isRequired,
};
